import { useState, useRef } from "react";
import { motion, useSpring, useMotionValue, useTransform } from "framer-motion";
import {  ArrowUpRight, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import blogdata from "../data/blogsdata"


const headerVariants = {
  hidden: { opacity: 0, y: 18 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
  },
};

const listVariants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.09, delayChildren: 0.1 },
  },
};

const rowVariants = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] },
  },
};

export default function BlogSection() {
  const [activeIdx, setActiveIdx] = useState(null);
  const containerRef = useRef(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const prevX = useRef(0);
  const springConfig = { damping: 22, stiffness: 200, mass: 0.6 };
  const smoothX = useSpring(mouseX, springConfig);
  const smoothY = useSpring(mouseY, springConfig);
  const velocityX = useMotionValue(0);
  const tiltSpring = useSpring(velocityX, { damping: 20, stiffness: 150 });
  const rotate = useTransform(tiltSpring, [-40, 40], [-8, 8]);

  const handleMouseMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    velocityX.set(x - prevX.current);
    prevX.current = x;
    mouseX.set(x);
    mouseY.set(e.clientY - rect.top);
  };

  return (
    <section
      ref={containerRef}
      onMouseMove={handleMouseMove}
      className="relative w-full lg:py-24 md:py-16 py-10 bg-[#f7f7f5] text-slate-900 overflow-hidden"
    >
      <motion.div
        style={{

          left: smoothX,
          top: smoothY,
          translateX: "-50%",
          translateY: "-50%",
          rotate,
        }}
        animate={{
          scale: activeIdx !== null ? 1 : 0.7,
          opacity: activeIdx !== null ? 1 : 0,
        }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className="pointer-events-none fixed md:absolute z-30 hidden md:block w-72 h-44 rounded-2xl overflow-hidden shadow-2xl border border-white/40 ring-1 ring-black/10 bg-slate-950 will-change-transform"
      >
        {blogdata.map((article, idx) => (
          <motion.img
            key={article.id}
            src={article.image}
            alt={article.title}
            initial={false}
            animate={{
              opacity: activeIdx === idx ? 1 : 0,
              scale: activeIdx === idx ? 1 : 1.12,
            }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0 w-full h-full object-cover grayscale contrast-115"
          />
        ))}
        <motion.div
          initial={false}
          animate={{ opacity: activeIdx !== null ? 1 : 0 }}
          transition={{ duration: 0.3 }}
          className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"
        />
      </motion.div>

      <div className="mx-auto 3xl:w-4/5 lg:w-11/12 px-6 lg:px-12 relative z-10">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={headerVariants}
          className="flex flex-col lg:flex-row lg:items-end justify-between pb-8 mb-12 border-b border-slate-300/80 gap-6"
        >
          <div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.05 }}
              className="inline-flex items-center gap-2 px-0.5 py-1.5  backdrop-blur-md shadow-xs mb-4"
            >
           
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-700">
                Index &amp; Publications
              </span>
            </motion.div>

            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-slate-950">
              Featured{" "}
              <span className="italic font-serif font-normal text-[#5dc9f0]">
                News & Insights
              </span>{" "}
            </h2>
          </div>
          <Link
            to="/blogs"
            className="group lg:w-auto w-52 relative inline-flex items-center justify-center md:justify-between gap-3 md:px-5 py-3 border border-black/15  bg-white/40 text-slate-900 font-mono text-xs font-semibold uppercase tracking-widest transition-all duration-300 hover:bg-slate-950 hover:text-white hover:shadow-lg"
            style={{
              borderRadius: "999px",
            }}
          >
            <span>vIEW ALL ARTICLE</span>
            <div className="h-7 w-7 rounded-full bg-white/10 flex items-center justify-center transition-transform duration-300 group-hover:translate-x-1  group-hover:bg-white group-hover:text-slate-950">
              <ArrowUpRight size={15} />
            </div>
          </Link>
        </motion.div>
        <motion.div
          onMouseLeave={() => setActiveIdx(null)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          variants={listVariants}
          className="divide-y divide-slate-300/80 border-b border-slate-300/80"
        >
          {blogdata.map((article, idx) => {
            const isActive = activeIdx === idx;

            return (
              <Link
                key={article.id}
                to={`/blogs/${article.slug}`}
                variants={rowVariants}
                onMouseEnter={() => setActiveIdx(idx)}
                // whileTap={{ scale: 0.99 }}
                className="group relative block py-10 md:py-14 transition-colors cursor-pointer overflow-hidden"
              >
                <motion.div
                  initial={false}
                  animate={{
                    opacity: isActive ? 1 : 0,
                    x: isActive ? "0%" : "-100%",
                  }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="pointer-events-none absolute inset-0 bg-transparent"
                />

                <div className="relative grid grid-cols-1 xl:grid-cols-12 gap-6 items-baseline">
                  
                  <div className="md:col-span-3 flex items-center gap-4">
                    <motion.span
                      animate={{
                        color: isActive ? "#0f172a" : "#94a3b8",
                        scale: isActive ? 1.1 : 1,
                      }}
                      transition={{ duration: 0.3 }}
                      className="font-mono text-sm font-bold inline-block"
                    >
                      0{idx + 1}
                    </motion.span>
                    <motion.span
                      animate={{
                        borderColor: isActive
                          ? "rgba(15,23,42,0.15)"
                          : "rgba(0,0,0,0.05)",
                        backgroundColor: isActive
                          ? "rgba(255,255,255,0.95)"
                          : "rgba(255,255,255,0.6)",
                      }}
                      transition={{ duration: 0.3 }}
                      className="text-sm font-mono uppercase tracking-wider text-slate-600 px-3 py-1 rounded-full border"
                    >
                      {article.tag}
                    </motion.span>
                  </div>
                  <div className="md:col-span-7">
                    <motion.h3
                      animate={{ x: isActive ? 8 : 0 }}
                      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                      className="font-display text-xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-950 leading-tight"
                    >
                      {article.title}
                    </motion.h3>

                    <motion.div
                      animate={{ scaleX: isActive ? 1 : 0 }}
                      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                      style={{ originX: 0 }}
                      className="h-px bg-slate-900 mt-3 w-16"
                    />

                    <motion.p
                      animate={{
                        height: isActive ? "auto" : 0,
                        opacity: isActive ? 1 : 0,
                        marginTop: isActive ? 12 : 0,
                      }}
                      transition={{ duration: 0.35, ease: "easeOut" }}
                      className="overflow-hidden text-sm sm:text-base text-slate-600 font-normal leading-relaxed max-w-2xl"
                    >
                      {article.summary}
                    </motion.p>
                  </div>
                  <div className="md:col-span-2 flex items-center justify-between md:justify-end gap-4 text-xs font-mono text-slate-400">
                    <span className="flex items-center gap-1.5">
                      <Clock size={12} />
                      {article.readTime}
                    </span>
                    <motion.div
                      animate={{
                        rotate: isActive ? 45 : 0,
                        backgroundColor: isActive
                          ? "#0f172a"
                          : "rgba(255,255,255,0)",
                        borderColor: isActive ? "#0f172a" : "#cbd5e1",
                        color: isActive ? "#ffffff" : "#0f172a",
                      }}
                      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                      className="h-10 w-10 rounded-full border flex items-center justify-center"
                    >
                      <ArrowUpRight size={15} />
                    </motion.div>
                  </div>
                </div>
              </Link>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
